import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '../api'

export interface Booking {
  id: string
  user_id: string
  title: string
  check_in: string
  check_out: string
  check_in_time: string
  check_out_time: string
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  occupancy_type: string
  num_guests: number
  notes: string | null
  preheat_enabled: boolean
  preheat_hours: number
  created_at: string
  source: string
  user_name?: string
  user_email?: string
}

export interface CreateBookingRequest {
  check_in: string
  check_out: string
  check_in_time?: string
  check_out_time?: string
  title?: string
  num_guests?: number
  notes?: string
  preheat_enabled?: boolean
  preheat_hours?: number
}

export const useBookingsStore = defineStore('bookings', () => {
  const bookings = ref<Booking[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const approvedBookings = computed(() =>
    bookings.value.filter(b => b.status === 'approved')
  )

  const pendingBookings = computed(() =>
    bookings.value.filter(b => b.status === 'pending')
  )

  async function fetchBookings(options?: { status?: string; from?: string; to?: string }) {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams()
      if (options?.status) params.set('status', options.status)
      if (options?.from) params.set('from', options.from)
      if (options?.to) params.set('to', options.to)

      const query = params.toString() ? `?${params}` : ''
      const response = await api.get(`/api/bookings${query}`)
      bookings.value = response.bookings
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch bookings'
    } finally {
      loading.value = false
    }
  }

  async function createBooking(data: CreateBookingRequest) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/api/bookings', data)
      await fetchBookings()
      return response.booking
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create booking'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function updateBooking(id: string, data: { status?: string; notes?: string }) {
    loading.value = true
    error.value = null

    try {
      await api.patch(`/api/bookings/${id}`, data)
      await fetchBookings()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update booking'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function cancelBooking(id: string) {
    loading.value = true
    error.value = null

    try {
      await api.delete(`/api/bookings/${id}`)
      await fetchBookings()
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to cancel booking'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    bookings,
    loading,
    error,
    approvedBookings,
    pendingBookings,
    fetchBookings,
    createBooking,
    updateBooking,
    cancelBooking,
  }
})
